import { useCallback, useEffect } from 'react';
import { useIsApple, useServices } from 'core/common/hooks';
import { ATTRS_ANALYTICS_FUNNEL_NAME, SpanName } from 'core/common/observability/entities';
import {
  installCampaign2SenderExperiment,
  trialEndPaymentWhatsappExperiment,
} from 'core/feature-flags/config';
import { useFeatureFlags } from 'core/feature-flags/hooks';
import { useFunnelName } from 'core/funnel/hooks';
import {
  CardBrand,
  CardType,
  Currency,
  PaymentError,
  PaymentFormType,
  PaymentOrderDescription,
  PaymentProcessSteps,
  PaymentType,
} from 'core/payments/entities';
import {
  useOrder,
  usePayment,
  usePaymentActiveProvidersFeature,
  usePaymentType,
} from 'core/payments/hooks';
import { useUser } from 'core/user/hooks';

type UsePaymentFormProps = {
  productId: string;
  onPaymentSuccess?: (data: { order?: PaymentOrderDescription }) => void;
  onPaymentFailed?: () => void;
  currency?: Currency;
  paymentFormType: PaymentFormType;
};

export const usePaymentForm = ({
  onPaymentFailed,
  onPaymentSuccess,
  productId,
  currency = Currency.USD,
  paymentFormType,
}: UsePaymentFormProps) => {
  const {
    analyticsService,
    loggingService: logger,
    observabilitySystem,
    appEnvironment,
  } = useServices();
  const { orderId } = useOrder();

  const { userPreferences } = useUser();
  const { email, nebulaId } = userPreferences;

  const { isApple } = useIsApple();
  const { funnelName } = useFunnelName();
  const { paymentForm, defaultMethod } = usePaymentActiveProvidersFeature();
  const featureFlags = useFeatureFlags();

  const {
    initializePaymentFormRequest,
    paymentFormVerify,
    paymentFormMerchantData,
    paymentFormPaypalData,
    paymentProcessStep,
    paymentError,
    clearPaymentState,
    paymentFormFailed: paymentFormFailedReceived,
    paymentProcessingData,
    paymentFormSucceed,
  } = usePayment({ offerId: productId, paymentFormType });

  const { paymentType: selectedPaymentType, updatePaymentType } = usePaymentType();

  const shouldRenderPaypal = paymentForm.paypal && !!paymentFormPaypalData.paypalScriptUrl;
  const shouldRenderCardPayment =
    paymentForm.card && !!paymentFormMerchantData.paymentForm.merchant;
  const shouldRenderApplePay = paymentForm.apple_pay && isApple;
  const shouldRenderPix = paymentForm.pix;

  const shouldRenderGooglePay = paymentForm.google_pay && appEnvironment.isAndroid();

  const paymentFormClick = (paymentType: PaymentType) => {
    analyticsService.trackPaymentFormState({ state: 'click', payment_method: paymentType });
    updatePaymentType(paymentType);
  };

  const paymentClick = (paymentType: PaymentType) => {
    analyticsService.trackEvent('funnel_screen_click', {
      payment_method: paymentType,
      screen_context: 'trial_billing',
    });
    updatePaymentType(paymentType);
  };

  const paypalFailed = useCallback(
    (data: PaymentError) => {
      logger.error(new Error('Paypal error'), {
        data,
        tags: { payment: true },
      });

      analyticsService.paymentError({
        reason: data.message,
        productId,
        paymentMethod: PaymentType.PAYPAL,
      });
      paymentFormFailedReceived(data);
      analyticsService.paymentRetryScreenOpened();

      if (onPaymentFailed) {
        onPaymentFailed();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analyticsService, logger, productId],
  );

  const paymentFormFailed = useCallback(
    (data: PaymentError) => {
      logger.error(new Error('Payment form error'), {
        data,
        tags: { payment: true },
      });

      analyticsService.paymentError({
        reason: data.message,
        productId,
        paymentMethod: selectedPaymentType ?? '',
      });
      paymentFormFailedReceived(data);
      analyticsService.paymentRetryScreenOpened();

      if (onPaymentFailed) {
        onPaymentFailed();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analyticsService, logger, productId, selectedPaymentType],
  );

  const paymentSucceed = useCallback(
    async (data: { order?: PaymentOrderDescription }, paymentMethod: string) => {
      if (!data.order) {
        logger.warn('Order for succeed payment not found', {
          data,
          tags: { payment: true },
        });

        return;
      }

      const paymentId =
        paymentMethod === PaymentType.PAYPAL
          ? paymentFormPaypalData.paymentId
          : paymentFormMerchantData.paymentId;

      const span = observabilitySystem.startSpan(SpanName.PAYMENT_SUCCEED);
      span.setType(paymentMethod);
      span.setAttribute(ATTRS_ANALYTICS_FUNNEL_NAME, funnelName);

      span.succeed();

      const { paymentMethodDetails } = await paymentFormSucceed(paymentId);

      const paymentCardType = paymentMethodDetails?.cardType ?? CardType.UNKNOWN;
      const paymentCardBrand = paymentMethodDetails?.cardBrand ?? CardBrand.UNKNOWN;

      featureFlags.getFeatureValue(installCampaign2SenderExperiment);
      featureFlags.getFeatureValue(trialEndPaymentWhatsappExperiment);

      analyticsService.paymentSuccess({
        orderId,
        paymentId,
        paymentProviderOrder: data.order,
        productId,
        paymentMethod,
        email,
        paymentCardBrand,
        paymentCardType,
        nebulaId,
      });

      if (onPaymentSuccess) {
        onPaymentSuccess(data);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onPaymentSuccess,
      analyticsService,
      logger,
      productId,
      currency,
      email,
      orderId,
      observabilitySystem,
      paymentFormSucceed,
      paymentFormMerchantData,
      paymentFormPaypalData,
    ],
  );

  const tryAgain = () => {
    if (paymentProcessStep !== PaymentProcessSteps.FAILED) return;

    analyticsService.paymentRetryTryAgainClicked();
    initializePaymentFormRequest();
  };

  const cardFormError = useCallback(
    (data: unknown) => {
      logger.error('Payment card form error', {
        data,
        tags: { payment: true },
      });
    },
    [logger],
  );

  const paymentMethodClose = useCallback(
    (paymentType: PaymentType) => {
      analyticsService.paymentMethodClose(paymentType);
    },
    [analyticsService],
  );

  useEffect(() => {
    updatePaymentType(defaultMethod);
  }, [defaultMethod, updatePaymentType]);

  useEffect(() => {
    if (orderId) {
      analyticsService.paymentFormOpened(orderId);
    }
  }, [analyticsService, orderId]);

  useEffect(() => {
    if (!productId) {
      return;
    }

    initializePaymentFormRequest();

    return () => clearPaymentState();
  }, [clearPaymentState, productId, initializePaymentFormRequest]);

  return {
    paymentError,
    shouldRenderPaypal,
    shouldRenderCardPayment,
    shouldRenderApplePay,
    shouldRenderPix,
    shouldRenderGooglePay,
    paymentProcessStep,
    paymentFormMerchantData,
    paymentFormPaypalData,
    paymentFormFailed,
    paymentSucceed,
    paypalFailed,
    paymentVerify: paymentFormVerify,
    cardFormError,
    paymentFormClick,
    paymentClick,
    tryAgain,
    paymentProcessingData,
    initializePaymentFormRequest,
    clearPaymentState,
    paymentMethodClose,
  };
};
